import { WMCodesLookupLayout } from '../../../../common/lookups/Layouts/WMLayouts';

/**
 * Dynamic-form schemas for the WM Charge Profile screen (WMCHARGESDP), migrated from the Delphi form
 * uWMD0008 "WM Charge Codes Profile Entry" (caption "WM Enter Charge Profiles"). Field labels, group
 * headings, widths and the User Defined code types are taken verbatim from uWMD0008.dfm and its data
 * module uWMM0008.dfm.
 */

const TABLE = 'WMCHARGESDP';

// Delphi: the five edtUserDefinedCodeN controls are lookups whose LookupSource is
// frmIMM0007.dsWMDescriptiveCodes - the WMCODES query, not IMCODES - restricted to their own
// CodeType. UserDefinedCodeLookup sets BaseWhere := 'WMCODES.CodeType = <n>'. The five lists are
// separate, not interchangeable, so each field gets its own layout.
//
// The values match Argos.Core/Constants/ChargeDpConstants.CodeTypes on the server side.
const userDefinedCodeTypes = ['62', '63', '64', '65', '66'];
// Delphi's WMCODES lookup grid (frmIMM0007.qryWMDescriptiveCodes) offers a narrower column set than
// the shared web layout: no ROWID, COMPANYID, MODULE, POPUPMESSAGE or CODETYPE - the query is already
// filtered to one company, module and code type, so those columns carry the same value on every row -
// and its two description fields are labelled "Description - 1" / "Description - 2".
//
// Derived from the shared layout rather than copied, so a column added there still flows through
// here. Scoped to this screen on purpose: WMCodesLookupLayout backs ~20 other screens that were not
// part of this migration and are not being changed.
const DELPHI_OMITTED_COLUMNS = ['id', 'companyid', 'module', 'popupmessage', 'codetype'];
const DELPHI_COLUMN_CAPTIONS = {
    description1: 'Description - 1',
    description2: 'Description - 2',
};

// Its own gridKey: the lookup grid persists column state per key (DynamicFromLookup.js:68), and the
// shared key would let another screen's saved layout - which still knows the omitted columns - be
// restored over this one.
const CODE_LOOKUP_GRID_KEY = '9E4E2542-AB5E-424A-A4E7-4A55AC2BF673';

const delphiCodesLookup = (codeType) => {
    const shared = WMCodesLookupLayout({ codeType });
    return {
        ...shared,
        gridKey: CODE_LOOKUP_GRID_KEY,
        columns: shared.columns
            .filter((column) => !DELPHI_OMITTED_COLUMNS.includes(column.dataField))
            .map((column) => (
                DELPHI_COLUMN_CAPTIONS[column.dataField]
                    ? { ...column, caption: DELPHI_COLUMN_CAPTIONS[column.dataField] }
                    : column
            )),
    };
};

const codeLookupLayouts = userDefinedCodeTypes.map((codeType) => delphiCodesLookup(codeType));

// These render as a searchable SelectBox *plus* the magnifier popup grid, on both the search panel
// and the add/edit form. That combination is what omitting `lookupDisplayMode` buys:
// DynamicFromLookup renders a bare TextBox with only the popup button when it is set to "grid"
// (DynamicFromLookup.js:443), and a SelectBox with the popup button appended when it is not - the
// button comes from WMCodesLookupLayout's params.showLookupGrid. Do not add lookupDisplayMode here.

/**
 * Find-screen search panel.
 *
 * One field per group on purpose: FormField renders every group as a Bootstrap `col-md-4`, i.e. three
 * groups to a row, so grouping two criteria together stacks them in one column and pushes the third
 * onto a second row. Three single-field groups put Record # / Profile / Description on one line, in
 * the order the Delphi form lists them. The hidden group that follows is a fourth column with
 * nothing visible in it.
 *
 * Delphi's navigator searched on CriteriaFields = (KEYSEQUENCE, PROFILECODE); Description and the
 * five codes are added because the listing grid exposes them. COMMENT is deliberately absent: FillFilters
 * emits a "contains" predicate, which compiles to String.Contains -> CHARINDEX, and COMMENT is a
 * SQL Server `text` column that rejects it.
 *
 * Field keys are camelCase while the DTO properties they filter on are PascalCase. FillFilters emits
 * the key verbatim, so this relies on DataSourceLoader resolving the selector case-insensitively -
 * the same thing the shipped itemDataProfile, weekSettings and exceptions search panels rely on.
 */
export const SearchLayout = [
    {
        "groupId": 1,
        "fields": [
            { "name": "recordNumber", "localizedField": "KEYSEQUENCE", "tableName": TABLE, "label": "Record #", "type": "text", "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        "groupId": 2,
        "fields": [
            { "name": "profileCode", "tableName": TABLE, "label": "Profile Code", "type": "text", "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        "groupId": 3,
        "fields": [
            { "name": "description", "tableName": TABLE, "label": "Description", "type": "text", "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        // Hidden on the listing grid, so hidden here too - the dynamic-form customiser then exposes
        // exactly the same set either way.
        "groupId": 4,
        "fields": codeLookupLayouts.map((layout, index) => ({
            "name": `userDefinedCode${index + 1}`,
            "localizedField": `USERDEFINEDCODE_${index + 1}`,
            "tableName": TABLE,
            "label": `Code ${index + 1}`,
            "type": "lookup",
            "layout": layout,
            "required": false,
            "visible": false,
            "isLabelChanged": false,
        }))
    }
];

/**
 * The Delphi "Primary" tab.
 *
 * uWMD0008 has no tab set: every control lives in a top panel, with a blank group box filling the
 * space below it. The web form renders that panel's two groups as the Primary tab's two columns, in
 * the same left-to-right order the desktop screen shows them:
 *
 *   grpTopLeft   - Record #, Profile, Description, Comment
 *   grpTopMiddle - "User Defined Codes", Code 1..5
 *
 * Widths follow the WMCHARGESDP columns, which agree with the TAbStringField Sizes in uWMM0008.dfm.
 */
export const PrimaryLayout = [
    {
        "groupId": 1,
        "label": "",
        "fields": [
            {
                "name": "RECORD",
                "localizedField": "KEYSEQUENCE",
                "tableName": TABLE,
                "label": "Record #",
                "type": "text",
                "required": false,
                "visible": true,
                "readOnly": true,
                "isLabelChanged": false,
            },
            {
                // "Profile Code" - the lblPROFILECODE caption in uWMD0008.dfm and the field's
                // DisplayLabel both read that, unlike the Crate Profile screen's plain "Profile".
                "name": "PROFILECODE",
                "tableName": TABLE,
                "label": "Profile Code",
                "type": "text",
                "required": true,
                // PROFILECODE is the CODE domain, length 10 in the table - which agrees with
                // uWMM0008.dfm. Matches ChargeDpConstants.FieldLengths.
                "maxLength": 10,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                "name": "DESCRIPTION",
                "tableName": TABLE,
                "label": "Description",
                "type": "text",
                "required": false,
                "maxLength": 40,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                // COMMENT is SQL `text` and the Delphi control is a TAbMemo, so it renders multiline
                // (rule 9).
                "name": "COMMENT",
                "tableName": TABLE,
                "label": "Comment",
                "type": "textarea",
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
        ]
    },
    {
        // Delphi grpTopMiddle, headed by the hdrUserDefined column label. WMCHARGESDP has no
        // user-defined date/quantity/reference columns, so there is no separate User Defined tab.
        "groupId": 2,
        "label": "User Defined Codes",
        "fields": codeLookupLayouts.map((layout, index) => ({
            "name": `USERDEFINEDCODE${index + 1}`,
            "localizedField": `USERDEFINEDCODE_${index + 1}`,
            "tableName": TABLE,
            "label": `Code ${index + 1}`,
            "type": "lookup",
            "layout": layout,
            "required": false,
            "maxLength": 10,
            "visible": true,
            "isLabelChanged": false,
        }))
    }
];
